import {Injectable} from '@angular/core';
import {CircleGeometry, Color, Face3, Geometry, Material, Mesh, MeshBasicMaterial, Vector2, Vector3} from 'three';

import {ContainerService} from '../../Utils/container.service';
import {PolyPaintService} from './poly-paint.service';
import {ColorCodingService} from './color-coding.service';
import {TexturePaintService} from './texture-paint.service';
import {MarkingType} from './markingType';
import {SceneService} from '../Scene/scene.service';
import {RenderService} from '../Scene/render.service';
import {CameraService} from '../Scene/camera.service';
import {MouseControlsService} from '../Control/mouse-controls.service';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {CanvasEvent} from '../Control/controls';
import {PaintService} from './paint.service';
import {unequalDependencies} from 'mathjs';


@Injectable({
    providedIn: 'root'
})
export class BrushService {

    public readonly paintControl = 'drawOnTextureBrush';
    public readonly eraseControl = 'erase';

    // get/set
    private _brushSize = 0.05;
    private _regions = false;
    private _roentgen = false;
    private _marginalFilter = false;

    // get
    private _brush: Mesh;
    private _pointsUnder: Vector3[] = [];
    private _pointsOver: Vector3[] = [];
    private _rawPointsUnder: Vector3[] = [];
    private _rawPointsOver: Vector3[] = [];

    private _lastPoint: Vector3 = null;
    private _lastRawPoint: Vector3 = null;
    private _rawPoints: Vector3[];

    constructor(private paintService: PaintService,
                private containerService: ContainerService,
                private polyService: PolyPaintService,
                private textureService: TexturePaintService,
                private colorCodingService: ColorCodingService,
                private cameraService: CameraService,
                private sceneService: SceneService,
                private renderService: RenderService,
                private mouseControlService: MouseControlsService,
                private editorControlService: EditorControlService) {
        this.registerControls();
    }

    get brushSize(): number {
        return this._brushSize;
    }

    set brushSize(value: number) {
        this._brushSize = value;
    }

    get regions() {
        return this._regions;
    }

    set regions(value: boolean) {
        this._regions = value;
        this._marginalFilter = false;
    }

    get roentgen() {
        return this._roentgen;
    }

    set roentgen(value: boolean) {
        this._roentgen = value;
    }

    get marginalFilter() {
        return this._marginalFilter;
    }

    set marginalFilter(value: boolean) {
        this._marginalFilter = value;
        this._regions = false;
    }

    get brush(): Mesh {
        return this._brush;
    }

    get pointsUnder() {
        return this._pointsUnder;
    }

    get pointsOver() {
        return this._pointsOver;
    }

    get rawPointsUnder() {
        return this._rawPointsUnder;
    }

    get rawPointsOver() {
        return this._rawPointsOver;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * fill area that is marked by brush
     */
    public fillSelectedArea(modelMesh: Mesh, rawPoints, erase: boolean = false) {
        this._rawPoints = rawPoints;

        // save original faces and uvs
        const faces = Array.from((<Geometry>modelMesh.geometry).faces);
        const uvs = Array.from((<Geometry>modelMesh.geometry).faceVertexUvs[0]);
        const ccFaces = Array.from((<Geometry>(<Mesh>this.containerService.polyPaintGroup.children[0]).geometry).faces);

        this.markRelevantLayers(modelMesh, erase);

        // restore faces and uvs
        if (this.roentgen) {
            this.resetGeometry(faces, ccFaces, uvs);
        }
    }

    /**
     * displays preview of brush size in render scene
     * @param value: brush size
     * @param height: preview position
     * @param transparent: opacity of the preview
     */
    public displayBrushSize(value: number, height, transparent = true) {
        this.mouseControlService.computeSelectionPlane();
        this.paintService.clearLassoPoints();
        this.paintService.startPaint();

        const start = this.mouseControlService.computePlaneIntersection(new Vector3(-0.01, height, 0.5));
        const end = this.mouseControlService.computePlaneIntersection(new Vector3(0.01, height, 0.5));

        this.paintService.addPenLine(value, start, end, !transparent, 0.6);
        this.renderService.render();
    }

    /**
     * fixates brush size and removes the indicator from scene after timeout
     * @param value: brush size
     * @param timeout: time for display brush size preview in render scene
     */
    public fixateBrushSize(value: number, timeout = 0) {
        this.brushSize = value;
        setTimeout(() => this.paintService.clearLassoPoints(), timeout);
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * registers editor controls for brush drawing
     * @private
     */
    private registerControls() {
        this.registerBrushControl(this.paintControl);
        this.registerBrushControl(this.eraseControl, true);
    }

    private registerBrushControl(name: string, erase: boolean = false) {
        this.editorControlService.registerControl(name, {
            events: new Map([
                [CanvasEvent.Down, event => {
                    const result = this.paintService.startPaintFromEvent(event, true, this.brushSize);
                    if (result) {
                        this.addBrushPoints(result.point, result.rawpoint, result.direction);
                    }
                }],
                [CanvasEvent.Move, event => {
                    const result = this.paintService.paintForEvent(event, true, this.brushSize);
                    if (result) {
                        this.addBrushPoints(result.point, result.rawpoint, result.direction);
                    }
                }],
                [CanvasEvent.Up, () => this.finishDrawing(erase)]
            ]),

            pan: false,
            controlMode: false
        });
    }

    private finishDrawing(erase = true) {
        if (this.paintService.rawpoints.length >= 1) {
            this.sortBrushPoints();
            const model = <Mesh> this.containerService.renderGroup.children[0];
            this.fillSelectedArea(model, this.paintService.rawpoints, erase);
            this.paintService.clearLassoPoints();
            this.clearBrushPoints();
        }
    }

    private markRelevantLayers(model: Mesh, erase: boolean) {
        if (!(this.containerService.loadingStatus !== 1 && this._regions)) {
            this.polyService.initIndices();

            const originalIndices = new Set<number>(); // face indices before deletion
            const pixelSet = new Set<number>();

            let layer = 0;
            let lastLayer = false;
            do {
                const res = this.markLayer(model, originalIndices, pixelSet, layer, erase);
                layer = res.layer;
                lastLayer = res.lastLayer;
            } while (this.roentgen && !lastLayer);

            const markType = new MarkingType();
            markType.color = this.paintService.paintColor.color;
            markType.burnDegree = this.paintService.paintColor.name;
            this.textureService.markPointInTexture(pixelSet, this.containerService.textureCanvas, erase, markType);
        }
    }

    /**
     *
     * @param model
     * @param originalIndices
     * @param pixelSet
     * @param layer
     * @param erase
     * @returns: whether this is the last layer
     */
    private markLayer(model: Mesh, originalIndices: Set<number>, pixelSet: Set<number>, layer: number, erase: boolean):
        {layer: number, lastLayer: boolean} {

        let layerFaces = [];
        this.colorCodingService.bufferScene.remove(this.containerService.helpPlane);
        const polySelection = this.polyService.brushSelection(model, this._rawPoints, this._brushSize, erase, this.roentgen);

        if (this._regions || this._marginalFilter) {
            polySelection.originalIndices.forEach(index => this.containerService.faceNeighbours.get(index)
                                                           .forEach(j => originalIndices.add(j)));

            if (this._marginalFilter) {
                this.polyService.placeFilterPlane(polySelection.originalIndices, model);
            }
            const regionSelection = this.regionSelection(originalIndices, model);
            regionSelection.pixelIndices.forEach(index => pixelSet.add(index));
            layerFaces = regionSelection.ignoredFaces;

            layer++;
        } else {
            const pixelIndices = Array.from(
                this.brushSelectionInvisible(model, this._rawPoints, this._brushSize).values()
            );
            pixelIndices.forEach(index => {
                pixelSet.add(index);
            });
        }

        if (this.roentgen) {
            this.decreaseSelectedArea(polySelection);

            // delete faces so that next layer is visible
            layerFaces = polySelection.currentIndices;
            this.deleteFaces(layerFaces);
            this.flipFaceNormals();
        }

        return {layer: layer, lastLayer: layerFaces.length === 0 || layer >= 2};
    }

    /**
     * adds points needed for bush drawing
     */
    private addBrushPoints(point: Vector3, rawpoint: Vector3, direction: Vector3) {
        if (this._lastPoint === null) {
            this._lastPoint = point.clone();
        }
        if (this._lastRawPoint === null) {
            this._lastRawPoint = rawpoint.clone();
        }

        const bs = this._brushSize;
        const np = this.calculateNormal(point, this._lastPoint);
        this._pointsUnder.push(new Vector3(point.x + np.x * bs, point.y + np.y * bs, point.z));
        this._pointsOver.push(new Vector3(point.x - np.x * bs, point.y - np.y * bs, point.z));

        // calculate rawpoint normal
        const npr = this.calculateNormal(rawpoint, this._lastRawPoint);

        // add rawpoints with offset to original rawpoint (in regard to normal)
        this.addRawPointOff(new Vector3(rawpoint.x + npr.x * bs * 100, rawpoint.y + npr.y * bs * 100, 0), true);
        this.addRawPointOff(new Vector3(rawpoint.x - npr.x * bs * 100, rawpoint.y - npr.y * bs * 100, 0), false);

        this._lastPoint = point.clone();
        this._lastRawPoint = rawpoint.clone();
    }

    private addRawPointOff(pt, under: boolean = true) {
        const rps = under ? this._rawPointsUnder : this._rawPointsOver;
        const found = rps.indexOf(pt) >= 0;

        if (!found) {
            rps.push(pt);
        }
    }

    /**
     * empties all collections of points needed for brush drawing
     */
    private clearBrushPoints() {
        this._pointsOver = [];
        this._pointsUnder = [];
        this._rawPointsUnder = [];
        this._rawPointsOver = [];
        this._lastPoint = null;
        this._lastRawPoint = null;
    }

    /**
     * makes connected poly out of brush points
     */
    private sortBrushPoints() {
        this.paintService._points = this._pointsUnder;
        this.paintService.rawpoints = this._rawPointsUnder;

        for (let i = this.paintService._points.length - 1; i >= 0; --i) {
            this.paintService._points.push(this._pointsOver[i]);
            this.paintService.addRawPoint(this._rawPointsOver[i]);
        }

    }

    // TODO: usage-check
    /**
     * marks area on texture selected by brush
     */
    // public brushSelection(modelMesh: Mesh, selPoints2D: any, brushSize: number) {
    //     const indexSet = this.brushSelectionInvisible(modelMesh, selPoints2D, brushSize);
    //     const markingType = new MarkingType();
    //     markingType.color = this.containerService.paintColor.color;
    //     this.textureService.markPointInTexture(indexSet, this.containerService.textureCanvas, false, markingType);
    //     this.containerService.texture.needsUpdate = true;
    //     // this.textureSizeService.change = true;
    //     this.renderService.render();
    //
    // }


    private calculateNormal(current: Vector3, previous: Vector3) {
        const dx = current.x - previous.x;
        const dy = current.y - previous.y;
        const v1 = new Vector3(dy, -dx, current.z);
        const v2 = new Vector3(-dy, dx, current.z);
        const v = new Vector3();
        v.subVectors(v2, v1).normalize();

        return v;
    }

    // TODO: move to brush service
    /**
     * returns indices of pixels in texture that are selected by brush
     * does not mark the pixels
     */
    public brushSelectionInvisible(modelMesh: Mesh, selPoints2D: any, brushsize: number, marginalfilter = false): Set<number> {
        this.colorCodingService.initBufferTexture(marginalfilter);
        const size = (Number(brushsize.toFixed(2))) ;

        // TODO: dynamic calculation of scaling factor (1000) based on camera distance
        const offset = this.colorCodingService.resFactor * 1000 * size;

        const [x, y, w, h] = this.colorCodingService.getCheckarea(selPoints2D, offset);

        this.colorCodingService.renderColorpaintImg(x, y, w, h, true);
        this.colorCodingService.initBufferSelectionArea();
        this.colorCodingService.renderColorpaintImg(x, y, w, h, false);
        return this.colorCodingService.getIndexindTexture();
    }

    /**
     * filters pixel indices in selection to only the region closest to camera
     * @param originalIndices
     * @param modelMesh
     * @private
     */
    private regionSelection(originalIndices, modelMesh: Mesh): {pixelIndices: Array<number>, ignoredFaces: Array<number>} {
        // get region closest to camera
        const ignoredFaces = this.polyService.getIgnoredIndices(Array.from(originalIndices.values()), modelMesh);

        // select only pixels in region closest to camera
        this.polyService.colorFaces(modelMesh, ignoredFaces, new Color(0, 0, 0));
        this.renderService.render();
        const pixelIndices = Array.from(this.brushSelectionInvisible(modelMesh,
                                        this._rawPoints, this._brushSize,
                                        this._marginalFilter).values());
        this.polyService.colorFaces(modelMesh, ignoredFaces, new Color());
        return {pixelIndices, ignoredFaces};
    }

    /**
     * reduce to area which includes less points outside of mesh
     * @param result
     * @private
     */
    private decreaseSelectedArea(result) {
        this._rawPoints = this.paintService.rawpoints.filter(
            (value) =>  value.x >= result.area[0] &&
                                value.y >= result.area[1] &&
                                value.x <= result.area[2] &&
                                value.y <= result.area[3]
        );
    }

    /**
     * deletes faces in one layer as part of the roentgen filter selection
     * @param indices
     */
    private deleteFaces(indices: number[]) {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        const ccModel = <Mesh>this.containerService.polyPaintGroup.children[0];

        for (let i = 0; i < indices.length; ++i) {
            (<Geometry>model.geometry).faces.splice(indices[i], 1);
            (<Geometry>ccModel.geometry).faces.splice(indices[i], 1);
            (<Geometry>model.geometry).faceVertexUvs[0].splice(indices[i], 1);
            this.polyService.indices.splice(indices[i], 1);
        }

        (<Geometry>model.geometry).elementsNeedUpdate = true;
        (<Geometry>model.geometry).uvsNeedUpdate = true;
    }

    /**
     * restore faces and uvs after deletion by roetgen filter
     * @param faces: faces of rendered mesh
     * @param ccFaces: faces of color coded mesh
     * @param uvs
     */
    private resetGeometry(faces: Face3[], ccFaces: Face3[], uvs: Vector2[][]) {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        const ccModel = <Mesh>this.containerService.polyPaintGroup.children[0];

        (<Geometry>model.geometry).faces = faces;
        (<Geometry>model.geometry).elementsNeedUpdate = true;
        (<Geometry>model.geometry).faceVertexUvs[0] = uvs;
        (<Geometry>model.geometry).uvsNeedUpdate = true;

        (<Geometry>ccModel.geometry).faces = ccFaces;
        (<Geometry>ccModel.geometry).elementsNeedUpdate = true;

        this.colorCodingService.initColors(<Geometry>(<Mesh>this.containerService.polyPaintGroup.children[0]).geometry);
    }

    /**
     * flips the face normals for the roentgen selection to recognize the inside of the faces
     */
    private flipFaceNormals() {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        const ccModel = <Mesh>this.containerService.polyPaintGroup.children[0];

        (<Material>model.material).side = 1 - (<Material>model.material).side;
        (<Material>ccModel.material).side = 1 - (<Material>model.material).side;

        this.colorCodingService.initColors(<Geometry>ccModel.geometry);
    }
}
